import { useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { Box, Container, IconButton, Link as MuiLink } from "@mui/material";
import logo from "../../assets/images/logo.png";

const navItems = [
    { label: "AKTUELL", type: "section", target: "aktuelles" },
    { label: "ÜBER UNS", type: "section", target: "ueber-uns" },
    { label: "SORTIMENT", type: "section", target: "sortiment" },
    { label: "MODE", type: "section", target: "mode" },
    { label: "TEE", type: "section", target: "tee" },
    { label: "KONTAKT", type: "section", target: "kontakt" },
    {
        label: "SHOP",
        type: "external",
        href: "https://www.hooksieler-laedchen.de",
    },
];

function Header() {
    const navigate = useNavigate();
    const location = useLocation();

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    function closeMenu() {
        setIsMenuOpen(false);
    }

    function toggleMenu() {
        setIsMenuOpen((prev) => !prev);
    }

    function scrollToSection(sectionId) {
        const element = document.getElementById(sectionId);

        if (!element) return;

        element.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }

    function handleSectionClick(event, sectionId) {
        event.preventDefault();
        closeMenu();

        if (location.pathname !== "/") {
            navigate("/");

            window.setTimeout(() => {
                scrollToSection(sectionId);
            }, 120);

            return;
        }

        scrollToSection(sectionId);
    }

    function renderNavItem(item, isMobile = false) {
        const commonStyles = {
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 900,
            letterSpacing: isMobile ? "0.08em" : "0.04em",
            textTransform: "uppercase",
            color: "#fff",
            lineHeight: 1,
            transition: "color 0.2s ease, padding 0.2s ease",

            "&:hover": {
                color: "#1d76bd",
                backgroundColor: "transparent",
            },
        };

        if (item.type === "external") {
            return (
                <MuiLink
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    onClick={closeMenu}
                    sx={{
                        ...commonStyles,
                        py: isMobile ? 1.35 : 0,
                        px: isMobile ? 2 : 0,
                        fontSize: isMobile
                            ? 15
                            : {
                                md: 12,
                                lg: 14,
                            },
                        borderBottom: isMobile
                            ? "1px solid rgba(255,255,255,0.14)"
                            : "none",
                    }}
                >
                    {item.label}
                </MuiLink>
            );
        }

        return (
            <MuiLink
                key={item.label}
                href={`/#${item.target}`}
                underline="none"
                onClick={(event) => handleSectionClick(event, item.target)}
                sx={{
                    ...commonStyles,
                    py: isMobile ? 1.35 : 0,
                    px: isMobile ? 2 : 0,
                    fontSize: isMobile
                        ? 15
                        : {
                            md: 12,
                            lg: 14,
                        },
                    color: item.target === "aktuelles" ? "#1d76bd" : "#fff",
                    borderBottom: isMobile
                        ? "1px solid rgba(255,255,255,0.14)"
                        : "none",

                    "&:hover": {
                        color: "#1d76bd",
                        backgroundColor: "transparent",
                        pl: isMobile ? 2.5 : 0,
                    },

                    "&:last-of-type": {
                        borderBottom: "none",
                    },
                }}
            >
                {item.label}
            </MuiLink>
        );
    }

    return (
        <Box
            component="header"
            sx={{
                backgroundColor: "transparent",
                width: "100%",
                position: "relative",
                zIndex: 20,
            }}
        >
            <Container
                maxWidth={false}
                sx={{
                    width: "100%",
                    maxWidth: 1200,
                    mx: "auto",
                    px: {
                        xs: 2,
                        sm: 3,
                        md: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        minHeight: {
                            xs: 88,
                            md: 135,
                        },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: {
                            xs: "space-between",
                            md: "center",
                        },
                        gap: {
                            xs: 2,
                            md: 5,
                            lg: 6,
                        },
                    }}
                >
                    <Box
                        component={RouterLink}
                        to="/"
                        onClick={closeMenu}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            textDecoration: "none",
                            flexShrink: 0,
                        }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="Hooksieler Lädchen Mode & Mee(h)r"
                            sx={{
                                width: {
                                    xs: 210,
                                    sm: 260,
                                    md: 300,
                                    lg: 310,
                                },
                                height: "auto",
                                display: "block",
                            }}
                        />
                    </Box>

                    <IconButton
                        onClick={toggleMenu}
                        aria-label={isMenuOpen ? "Menü schließen" : "Menü öffnen"}
                        aria-expanded={isMenuOpen}
                        sx={{
                            display: {
                                xs: "flex",
                                md: "none",
                            },
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            p: 0,
                            backgroundColor: "rgba(255,250,242,0.82)",
                            backdropFilter: "blur(8px)",

                            "&:hover": {
                                backgroundColor: "rgba(255,250,242,0.95)",
                            },
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                position: "relative",
                                width: 26,
                                height: 18,
                                display: "block",

                                "&::before, &::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    width: "100%",
                                    height: 3,
                                    backgroundColor: "#1d76bd",
                                    borderRadius: 2,
                                    transition: "all 0.25s ease",
                                },

                                "&::before": {
                                    top: isMenuOpen ? 7.5 : 0,
                                    transform: isMenuOpen
                                        ? "rotate(45deg)"
                                        : "rotate(0deg)",
                                },

                                "&::after": {
                                    bottom: isMenuOpen ? 7.5 : 0,
                                    transform: isMenuOpen
                                        ? "rotate(-45deg)"
                                        : "rotate(0deg)",
                                },
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: "50%",
                                    width: "100%",
                                    height: 3,
                                    backgroundColor: "#1d76bd",
                                    borderRadius: 2,
                                    transform: "translateY(-50%)",
                                    opacity: isMenuOpen ? 0 : 1,
                                    transition: "opacity 0.2s ease",
                                }}
                            />
                        </Box>
                    </IconButton>

                    <Box
                        component="nav"
                        aria-label="Hauptnavigation"
                        sx={{
                            display: {
                                xs: "none",
                                md: "flex",
                            },
                            alignItems: "center",
                            gap: {
                                md: 2.5,
                                lg: 3.6,
                            },
                            whiteSpace: "nowrap",
                        }}
                    >
                        {navItems.map((item) => renderNavItem(item))}
                    </Box>
                </Box>

                <Box
                    component="nav"
                    aria-label="Mobile Navigation"
                    sx={{
                        display: {
                            xs: "grid",
                            md: "none",
                        },
                        gridTemplateRows: isMenuOpen ? "1fr" : "0fr",
                        transition: "grid-template-rows 0.3s ease",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            minHeight: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                py: 1.5,
                                mb: 2,
                                backgroundColor: "rgba(42, 38, 33, 0.82)",
                                backdropFilter: "blur(12px)",
                                borderRadius: 3,
                                boxShadow: "0 18px 50px rgba(0,0,0,0.18)",
                            }}
                        >
                            {navItems.map((item) => renderNavItem(item, true))}
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}

export default Header;